import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { seriesAPI, showAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Trash, Trophy, Calendar, MapPin } from '@phosphor-icons/react';
import { toast } from 'sonner';

const SeriesManage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [seriesList, setSeriesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSeries, setNewSeries] = useState({ name: '', description: '' });
  const [customPoints, setCustomPoints] = useState({
    1: 10, 2: 8, 3: 6, 4: 5, 5: 4, 6: 3, 7: 2, 8: 1, 9: 1, 10: 1
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchSeries();
  }, []);

  const fetchSeries = async () => {
    try {
      const { data } = await seriesAPI.list();
      setSeriesList(data);
    } catch {
      toast.error('Failed to load series');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newSeries.name.trim()) {
      toast.error('Series name is required');
      return;
    }
    setCreating(true);
    try {
      await seriesAPI.create({
        name: newSeries.name,
        description: newSeries.description,
        points_table: customPoints
      });
      toast.success('Series created!');
      setShowCreateModal(false);
      setNewSeries({ name: '', description: '' });
      fetchSeries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create series');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#9E9E9E]">Loading series...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8 px-4" data-testid="series-manage-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black text-white" style={{fontFamily: 'Outfit'}}>
              Show Series
            </h1>
            <p className="text-[#9E9E9E] mt-1">Track points across multiple shows</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-[#161616] border border-[#262626] hover:border-[#0096FF] text-white rounded-md transition-colors text-sm"
            >
              Back
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              data-testid="create-series-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-colors text-sm"
            >
              <Plus size={16} weight="bold" /> New Series
            </button>
          </div>
        </div>

        {/* Series List */}
        {seriesList.length === 0 ? (
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-12 text-center">
            <Trophy size={48} className="text-[#262626] mx-auto mb-4" />
            <p className="text-[#9E9E9E] mb-2">No series yet</p>
            <p className="text-[#525252] text-sm">Create a series to track cumulative standings across multiple shows</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {seriesList.map(s => (
              <div
                key={s.id}
                onClick={() => navigate(`/series/${s.id}`)}
                data-testid={`series-card-${s.id}`}
                className="bg-[#161616] border border-[#262626] hover:border-[#0096FF]/50 rounded-lg p-6 cursor-pointer transition-all group"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-white group-hover:text-[#0096FF] transition-colors" style={{fontFamily: 'Outfit'}}>
                      {s.name}
                    </h3>
                    {s.description && <p className="text-[#9E9E9E] text-sm mt-1">{s.description}</p>}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    s.status === 'ACTIVE' ? 'bg-[#34C759]/20 text-[#34C759]' : 'bg-[#9E9E9E]/20 text-[#9E9E9E]'
                  }`}>
                    {s.status}
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-3 text-sm text-[#9E9E9E]">
                  <span className="flex items-center gap-1">
                    <Calendar size={14} /> {s.show_count} show{s.show_count !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
            <div className="bg-[#161616] border border-[#262626] rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-series-modal">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-white mb-6" style={{fontFamily: 'Outfit'}}>Create Series</h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Series Name *</label>
                    <input
                      type="text"
                      value={newSeries.name}
                      onChange={(e) => setNewSeries({...newSeries, name: e.target.value})}
                      data-testid="series-name-input"
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF]"
                      placeholder="e.g., 2026 Spring Barrel Racing Series"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Description</label>
                    <textarea
                      value={newSeries.description}
                      onChange={(e) => setNewSeries({...newSeries, description: e.target.value})}
                      data-testid="series-description-input"
                      rows={2}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF]"
                      placeholder="Optional description"
                    />
                  </div>

                  {/* Points Table */}
                  <div>
                    <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Points Per Place</label>
                    <div className="grid grid-cols-5 gap-2">
                      {Object.entries(customPoints).map(([place, pts]) => (
                        <div key={place} className="flex flex-col items-center">
                          <span className="text-xs text-[#9E9E9E] mb-1">
                            {place === '1' ? '1st' : place === '2' ? '2nd' : place === '3' ? '3rd' : `${place}th`}
                          </span>
                          <input
                            type="number"
                            min="0"
                            value={pts}
                            onChange={(e) => setCustomPoints({...customPoints, [place]: parseInt(e.target.value) || 0})}
                            data-testid={`points-place-${place}`}
                            className="w-full px-2 py-2 bg-[#0A0A0A] border border-[#262626] rounded text-white text-center text-sm focus:outline-none focus:border-[#0096FF]"
                          />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-[#9E9E9E] mt-2">Customize how many points riders earn per placement</p>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-3 bg-transparent border border-[#262626] hover:border-[#0096FF] text-white rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreate}
                    disabled={creating}
                    data-testid="submit-series-btn"
                    className="flex-1 px-4 py-3 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-colors disabled:opacity-50"
                  >
                    {creating ? 'Creating...' : 'Create Series'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeriesManage;
